import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Col from 'reactstrap/lib/Col';
import Row from 'reactstrap/lib/Row';
import { get, compact, flatten } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { buildReviewPath, EditorialReviewModel, EditorialReviewEntities } from 'client/data/models/editorial-review';
import { buildWhatsNewPath, VehicleDetailsEntities, VehicleDetailsModel } from 'client/data/models/mmy-details';
import { buildVideosPath, MediaModel } from 'client/data/models/media';
import { AnchorNavContent } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-content';
import { CorePageParams, getParamsFromVehicle } from 'site-modules/shared/utils/core-page/params';
import {
  OLD_REVIEW_FORMAT_LAST_YEAR,
  RATINGS_MAP,
  TEXT_REVIEW_FORMAT_LAST_YEAR,
} from 'site-modules/shared/constants/editorial-review';
import { USED_ITEMS } from 'site-modules/core-page/components/editorial-review/utils';
import { MENU_HASH } from 'site-modules/shared/constants/core-page/menu-hash';
import {
  addRatingsV4NamesAndQuestions,
  getModelSectionMap,
  KEY_TO_HASH,
} from 'site-modules/shared/utils/core-page/editorial-review';
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';

import './used-core-subnav.scss';

function UsedCoreSubnavUI({
  params,
  vehicle: { edmundsTypeCategories },
  className = null,
  linkClassName = null,
  isMobile = false,
  editorialReview = null,
  whatsNewContent = null,
  hasVideos = false,
  isMainContentWidth = false,
  ...restProps
}) {
  const props = {
    ...restProps,
    editorialReview,
    whatsNewContent,
    hasVideos,
  };

  const ratingScorecardSections =
    params?.year > OLD_REVIEW_FORMAT_LAST_YEAR &&
    compact(
      (
        addRatingsV4NamesAndQuestions({ sections: props?.editorialReview?.ratingsV4?.ratingSections, params }) ||
        getModelSectionMap({
          edmundsTypeCategories,
          params,
          isScorecardV4: props?.editorialReview?.isScorecardV4,
        })
      ).filter(
        ({ key, score }) =>
          Object.keys(KEY_TO_HASH).includes(key) && (score || props?.editorialReview?.ratings?.[key]?.rating)
      )
    );

  const groupedReviewSections =
    params?.year <= OLD_REVIEW_FORMAT_LAST_YEAR &&
    params?.year > TEXT_REVIEW_FORMAT_LAST_YEAR &&
    [RATINGS_MAP.DRIVING, RATINGS_MAP.COMFORT, RATINGS_MAP.INTERIOR, RATINGS_MAP.UTILITY, RATINGS_MAP.TECHNOLOGY]
      .filter(({ value }) => props?.editorialReview?.ratings?.[value])
      .map(({ title, value }) => ({ name: title, key: value }));

  const textReviewSections =
    params?.year <= TEXT_REVIEW_FORMAT_LAST_YEAR &&
    [
      { title: 'Performance & mpg', id: 'mpg', exists: props?.editorialReview?.powertrain },
      { title: 'Safety', id: 'safety', exists: props?.editorialReview?.safety },
      ...USED_ITEMS,
    ]
      .filter(
        ({ id, exists, value }) =>
          Object.keys(KEY_TO_HASH).includes(id) && (exists || props?.editorialReview?.ratings?.[value]?.text)
      )
      .map(({ title, id }) => ({ name: title, key: id }));

  const SUBNAV_ITEMS = compact(
    flatten([
      { name: 'Reliability', hash: MENU_HASH.RELIABILITY },
      { name: 'Consumer Reviews', hash: MENU_HASH.CONSUMER_REVIEWS },
      { name: 'For Sale', hash: MENU_HASH.FOR_SALE },
      !isMobile && { name: 'Price & Payment', hash: MENU_HASH.TRIM_SUMMARY_MOBILE },
      { name: 'Pros & Cons', hash: MENU_HASH.PROS_CONS, dataPath: ['editorialReview.pros.length'] },
      {
        name: "What's New",
        hash: MENU_HASH.WHATS_NEW,
        dataPath: ['whatsNewContent.length', 'editorialReview.whatsNew'],
      },
      {
        name: 'Overview',
        hash: MENU_HASH.MODEL_OVERVIEW,
        dataPath: ['editorialReview.summary', 'editorialReview.introduction', 'editorialReview.review'],
      },
      compact([ratingScorecardSections, groupedReviewSections, textReviewSections])?.[0]?.map?.(({ name, key }) => ({
        name,
        hash: KEY_TO_HASH[key],
      })),
      {
        name: 'Models',
        hash: MENU_HASH.TRIM_DESCRIPTIONS,
        dataPath: ['editorialReview.trimFeaturesBodySnippet', 'editorialReview.body'],
      },
      {
        name: 'Recommended',
        hash: MENU_HASH.RECOMMENDED_TRIM,
        dataPath: ['editorialReview.weRecommend'],
      },
      params.isElectric && {
        name: 'EV Insights',
        hash: MENU_HASH.EV_INSIGHTS,
      },
      {
        name: 'Videos',
        hash: MENU_HASH.VIDEOS,
        dataPath: ['hasVideos'],
      },
      {
        name: 'Specs',
        hash: MENU_HASH.FEATURES_SPECS,
      },
      { name: 'Compare', hash: MENU_HASH.COMPETITION },
      {
        name: 'Safety',
        hash: MENU_HASH.SAFETY,
      },
    ])
  );

  const anchorNavContentProps = {
    navList: SUBNAV_ITEMS.filter(({ dataPath }) => !dataPath || dataPath.some(path => get(props, path))),
    activeClass: 'link-active heading-5',
    listClassName: 'd-flex gap-1 gap-md-2 ms-0',
    linkClassName: classnames('pt-1 pb-0_75', linkClassName),
    arrowClassName: classnames(
      'icon btn btn-link border-0 text-decoration-none text-primary-darker h-100 px-0_5 px-md-0_75',
      'size-16',
      linkClassName
    ),
    itemClassName: 'size-16',
    useNoFollow: false,
  };

  const stickyFragment = (
    <AnchorNavContent
      {...anchorNavContentProps}
      className="sticky border-0"
      linkClassName={undefined}
      arrowClassName={undefined}
      linkTrackingId="view_content_docked"
      isSticky
    />
  );

  return (
    <AnimatedSticky
      stickyFragment={
        isMainContentWidth ? (
          <Row>
            <Col xs={12} lg={8}>
              {stickyFragment}
            </Col>
          </Row>
        ) : (
          stickyFragment
        )
      }
      identifierClass={classnames('used-core-subnav', className)}
      creativeId="edm-entry-sub-nav"
    >
      <AnchorNavContent {...anchorNavContentProps} linkTrackingId="view_content_non_docked" />
    </AnimatedSticky>
  );
}

UsedCoreSubnavUI.propTypes = {
  params: CorePageParams.isRequired,
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  className: PropTypes.string,
  linkClassName: PropTypes.string,
  editorialReview: EditorialReviewEntities.EditorialReview,
  whatsNewContent: VehicleDetailsEntities.WhatsNewContent,
  hasVideos: PropTypes.bool,
  isMainContentWidth: PropTypes.bool,
  isMobile: PropTypes.bool,
};

export const UsedCoreSubnav = connectToModel(UsedCoreSubnavUI, {
  editorialReview: bindToPath(({ vehicle }) => buildReviewPath(getParamsFromVehicle(vehicle)), EditorialReviewModel),
  whatsNewContent: bindToPath(({ params }) => buildWhatsNewPath(params), VehicleDetailsModel),
  hasVideos: bindToPath(
    ({ vehicle }) => buildVideosPath({ make: vehicle.make.slug, model: vehicle.model.slug, year: vehicle.year }),
    MediaModel,
    videos => !!videos?.filter?.(video => video?.videoId)?.length
  ),
});
